<template>
  <div>
    <div class="lx-toolbar pt-0">
      <span class="kicker">Quarterly macro assumptions</span>
      <span class="lx-method">end-of-quarter values · GDP growth is a quarterly average</span>
      <div class="lx-toolbar__spacer"></div>
      <span v-if="changed.length" class="lx-method" aria-live="polite">
        {{ changed.length }} cell{{ changed.length === 1 ? "" : "s" }} modified vs consensus
      </span>
      <button type="button" class="lx-btn lx-btn--sm" :disabled="!changed.length" aria-label="Reset all inputs to the market consensus" @click="$emit('reset-all')">
        Reset all
      </button>
    </div>

    <v-card variant="outlined">
      <div class="lx-scroll-x">
        <v-table class="lx-inputs">
          <thead>
            <tr>
              <th scope="col" class="text-left" style="width: 20%">Indicator</th>
              <th v-for="q in quarters" :key="q" scope="col" class="text-center">{{ q }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in rows" :key="row.name">
              <th v-if="row.header" scope="row" class="text-left lx-row-group" :colspan="quarters.length + 1">{{ row.name }}</th>
              <template v-else>
                <th scope="row" class="text-left lx-rowhead">
                  <span>{{ row.name }}</span>
                  <span v-if="unitOf(row.name)" class="lx-unit">{{ unitOf(row.name) }}</span>
                  <button
                    v-if="rowChanged(i)"
                    type="button"
                    class="lx-rowreset"
                    :aria-label="'Reset ' + row.name + ' to consensus'"
                    @click="$emit('reset-row', i)"
                  >
                    reset
                  </button>
                </th>
                <td
                  v-for="(value, j) in row.data"
                  :key="j"
                  class="text-center"
                  :class="{ 'lx-cell--modified': isModified(i, j), 'lx-cell--warn': issueAt(i, j) }"
                >
                  <input
                    type="number"
                    step="0.1"
                    :value="value"
                    :disabled="isDisabled(row)"
                    :aria-label="row.name + ' ' + quarters[j]"
                    :aria-invalid="issueAt(i, j) ? 'true' : null"
                    :title="cellTitle(i, j)"
                    @change="onChange($event, i, j)"
                  />
                  <span v-if="isModified(i, j)" class="lx-cell__mark" aria-hidden="true">●</span>
                </td>
              </template>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>

    <ul v-if="issues.length" class="lx-warnlist" aria-live="polite">
      <li v-for="(w, k) in issues.slice(0, 6)" :key="k">
        <b>{{ w.name }} · {{ quarters[w.colIndex] }}</b>: {{ w.message }}
      </li>
      <li v-if="issues.length > 6">… and {{ issues.length - 6 }} more. Warnings never block Apply.</li>
    </ul>
  </div>
</template>

<script>
import { changedCells } from "../lib/scenario";
import { validateRows } from "../lib/validation";
import { inputUnit } from "../lib/format";

export default {
  name: "ForecastInputsTable",
  props: {
    quarters: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    originals: { type: Array, default: () => [] },
    disabledNames: { type: Array, default: () => [] },
  },
  emits: ["update-cell", "reset-row", "reset-all"],
  computed: {
    changed() {
      return changedCells(this.rows, this.originals, this.quarters);
    },
    changedKeys() {
      return new Set(this.changed.map((c) => c.rowIndex + ":" + c.colIndex));
    },
    issues() {
      return validateRows(this.rows);
    },
    issueKeys() {
      const m = new Map();
      for (const w of this.issues) m.set(w.rowIndex + ":" + w.colIndex, w);
      return m;
    },
  },
  methods: {
    unitOf: inputUnit,
    isModified(i, j) {
      return this.changedKeys.has(i + ":" + j);
    },
    rowChanged(i) {
      return this.changed.some((c) => c.rowIndex === i);
    },
    issueAt(i, j) {
      return this.issueKeys.get(i + ":" + j) || null;
    },
    isDisabled(row) {
      return row.disable === true || this.disabledNames.includes(row.name);
    },
    cellTitle(i, j) {
      const parts = [];
      if (this.isModified(i, j)) {
        const orig = this.originals[i] && this.originals[i].data[j];
        parts.push(`Consensus: ${orig}`);
      }
      const w = this.issueAt(i, j);
      if (w) parts.push(w.message);
      return parts.join(" · ") || null;
    },
    onChange(ev, i, j) {
      this.$emit("update-cell", { rowIndex: i, colIndex: j, value: ev.target.value });
    },
  },
};
</script>

<style scoped>
/* frozen indicator column so the row label stays readable while scrolling quarters */
.lx-inputs thead th { min-width: 96px; }
.lx-inputs tbody th.lx-rowhead,
.lx-inputs thead th:first-child {
  position: sticky; left: 0; z-index: 1; min-width: 260px;
  box-shadow: inset -1px 0 0 var(--line);
}
.lx-inputs tbody th.lx-rowhead { background: var(--bg-1); font-weight: 500; color: var(--text); font-family: var(--f-body); text-transform: none; letter-spacing: 0; font-size: 13px; white-space: nowrap; }
.lx-inputs thead th:first-child { background: var(--bg-2); }
.lx-inputs tbody th.lx-row-group { position: sticky; left: 0; z-index: 1; }
.lx-inputs td { position: relative; }
.lx-inputs input[type="number"]::-webkit-inner-spin-button,
.lx-inputs input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.lx-inputs input[type="number"] { -moz-appearance: textfield; }
</style>
